/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from 'react';
import QRCode from 'react-qr-code';

import {
  Grid,
  Paper,
  Table,
  TableContainer,
  TableRow,
  tableCellClasses,
} from '@mui/material';
import JsBarcode from 'jsbarcode';

import { formatCurrency } from 'commons/utils/formatCurrency';

import { IRenderedSTOItem, ISTOInvoiceMappedData } from './interfaces';
import S from './STOInvoicePrintTemplate.styles';

interface STOInvoicePrintTemplateComponentProps {
  data: ISTOInvoiceMappedData;
}

const BarcodeImage = ({ value }: { value: string }) => {
  const barcodeRef = React.useRef(null);

  useEffect(() => {
    JsBarcode(barcodeRef.current, value, {
      format: 'CODE128',
      width: 1.5,
      height: 50,
      displayValue: true,
      fontSize: 10,
    });
  }, [value]);

  return <img ref={barcodeRef} alt="Barcode" />;
};

const getMaxPageNumber = (tableData: IRenderedSTOItem[][]): number => {
  const additionalPage = tableData[tableData.length - 1].length > 10 ? 1 : 0;

  return tableData.length + additionalPage;
};

export const STOInvoicePrintTemplateComponent = ({
  data,
}: STOInvoicePrintTemplateComponentProps): React.JSX.Element => {
  const [maxPage, setMaxPage] = useState<number>(0);
  const renderHeader = () => (
    <S.PageHeader>
      {/* Invoice Cope Section */}
      <S.InvoiceCopeWrapper container>
        <Grid item xs={1}>
          <img
            src="/assets/print-template/TMPLogo.png"
            alt="logo"
            style={{
              width: '250%',
              marginTop: '24px',
              marginLeft: '-40px',
            }}
          />
        </Grid>
        {/* Middle Section Company Profile */}
        <S.CompanyProfileWrapper item xs={6}>
          <div>
            <b>{data.from_warehouse_info?.name}</b>
          </div>
          <S.CompanyAddressText>
            {data.from_warehouse_info?.address}
          </S.CompanyAddressText>
          <div>No Izin PBF: {data.from_warehouse_info?.pbf_no}</div>
          <div>No Izin DAK: {data.from_warehouse_info?.dak_no}</div>
          <div>No Sertifikat CDOB : {data.from_warehouse_info?.cdob_no}</div>
          <div>No Sertifikat CDAKB : {data.from_warehouse_info?.cdakb_no}</div>
          {data.from_warehouse_info?.cdob_ccp_no && (
            <div>
              No Sertifikat CDOB CCP : {data.from_warehouse_info?.cdob_ccp_no}
            </div>
          )}
        </S.CompanyProfileWrapper>
        {/* Right Section Nomor TO etc */}
        <Grid item xs={5} style={{ paddingTop: 4 }}>
          <Grid container>
            <Grid item xs={4}>
              <b>Nomor Transfer Order</b>
            </Grid>
            <Grid item xs={1}>
              :
            </Grid>
            <Grid item xs={7}>
              {data.sto_number}
            </Grid>
            {data.is_alkes ? (
              <Grid item xs={12}>
                <S.OrderTypeText>Alkes</S.OrderTypeText>
              </Grid>
            ) : data.is_prekursor ? (
              <Grid item xs={12}>
                <S.OrderTypeText>Prekursor</S.OrderTypeText>
              </Grid>
            ) : data.is_oot ? (
              <Grid item xs={12}>
                <S.OrderTypeText>Obat-Obat Tertentu</S.OrderTypeText>
              </Grid>
            ) : (
              ''
            )}
            <Grid item xs={4}>
              Tanggal
            </Grid>
            <Grid item xs={1}>
              :
            </Grid>
            <Grid item xs={7}>
              {data.invoice_date}
            </Grid>
          </Grid>
        </Grid>
      </S.InvoiceCopeWrapper>

      {/* Customer Info Section */}
      <S.CustomerInfoWrapper container>
        <S.CustomerInfoContent item xs={1}>
          <b>Customer</b>
          <span style={{ marginLeft: 4 }}>:</span>
        </S.CustomerInfoContent>
        <S.CustomerInfoContent item xs={11}>
          {data.to_warehouse_name}
        </S.CustomerInfoContent>
        <S.CustomerInfoContent item xs={1}>
          <b>Alamat</b>
          <span style={{ marginLeft: 20 }}>:</span>
        </S.CustomerInfoContent>
        <S.CustomerInfoContent item xs={11}>
          {data.to_warehouse_address}
        </S.CustomerInfoContent>
      </S.CustomerInfoWrapper>
    </S.PageHeader>
  );

  // determine max page data
  useEffect(() => {
    const maxPageNumber = getMaxPageNumber(data.items);
    setMaxPage(maxPageNumber);
  }, [maxPage, data.items]);

  // Footer Section
  const renderFooter = () =>
    Array.from(Array(maxPage).keys()).map((idx) => (
      <S.PageFooter className="last-page" pageOffset={idx}>
        <Grid container>
          <Grid item xs={3}>
            <QRCode size={72} value={`${data.sto_number}`} />
          </Grid>
          <Grid item xs={7} className="print-barcode">
            <BarcodeImage value={data.sto_number || '-'} />
          </Grid>
          <Grid item xs={2} style={{ textAlign: 'right' }}>
            <S.PageNumberText>
              Page {idx + 1} of {maxPage}
            </S.PageNumberText>
          </Grid>
        </Grid>
      </S.PageFooter>
    ));

  const renderBody = (itemData: IRenderedSTOItem[], isLastPage: boolean) => (
    <S.PageBody>
      <TableContainer component={Paper} style={{ boxShadow: 'none' }}>
        <Table
          sx={{
            minWidth: 650,
            [`& .${tableCellClasses.root}`]: {
              borderBottom: 'none',
              fontFamily: 'Arial',
              fontSize: 12,
            },
          }}
          size="small"
          aria-label="a dense table"
        >
          <S.TableHeadStyle>
            <TableRow>
              <S.TableHeaderCell width={75}>SKU</S.TableHeaderCell>
              <S.TableHeaderCell width={190}>NAMA BARANG</S.TableHeaderCell>
              <S.TableHeaderCell width={25}>QTY</S.TableHeaderCell>
              <S.TableHeaderCell width={130}>UOM</S.TableHeaderCell>
              <S.TableHeaderCell width={160}>Batch</S.TableHeaderCell>
              <S.TableHeaderCell width={55}>ED</S.TableHeaderCell>
              <S.TableHeaderCell width={75} align="right">
                Unit Price
              </S.TableHeaderCell>
              <S.TableHeaderCell width={80} align="right">
                Total
              </S.TableHeaderCell>
            </TableRow>
          </S.TableHeadStyle>
          <S.TableBodyStyle>
            {itemData.map((productInfo, idx) => (
              <S.TableRowStyle
                key={`InvRow-${productInfo.sku_code}-${idx}`}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                }}
              >
                <S.TableBodyCell>{productInfo.sku_code}</S.TableBodyCell>
                <S.TableBodyCell>{productInfo.name}</S.TableBodyCell>
                <S.TableBodyCell align="center">
                  {productInfo.quantity}
                </S.TableBodyCell>
                <S.TableBodyCell>{productInfo.uom}</S.TableBodyCell>
                <S.TableBodyCell>{productInfo.inventory}</S.TableBodyCell>
                <S.TableBodyCell>{productInfo.expiry_date}</S.TableBodyCell>
                <S.TableBodyCell align="right">
                  {formatCurrency(productInfo.price, false)}
                </S.TableBodyCell>
                <S.TableBodyCell align="right">
                  {formatCurrency(productInfo.net_price, false)}
                </S.TableBodyCell>
              </S.TableRowStyle>
            ))}
          </S.TableBodyStyle>
        </Table>
      </TableContainer>
      {/* If not last page  */}
      {!isLastPage ? (
        <>
          <div className="pagebreak-after" />
          <div style={{ width: '100%' }} />
        </>
      ) : (
        <>
          {/* Additional page break if maxPage > data.items.length
           because rest of page will not enough to render summary section. Summary section will 
           be rendered separately in the next page */}
          {maxPage > data.items.length && (
            <>
              <div className="pagebreak-after" />
              <div style={{ width: '100%' }} />
            </>
          )}
          {/* Summary Section */}
          <S.SummarySectionWrapper container>
            <Grid container item xs={12} justifyContent="flex-end">
              <Grid item xs={1.5}>
                <b>Total Harga</b>
              </Grid>
              <Grid item xs={1}>
                <b>:</b>
              </Grid>
              <Grid item xs={2} textAlign="right">
                {formatCurrency(data.total_amount)}
              </Grid>
            </Grid>
            {/* Signature Section */}
            <S.SignatureSectionWrapper item xs={5}>
              <Grid container>
                <Grid item xs={4} style={{ display: 'flex' }}>
                  <p style={{ alignSelf: 'flex-end' }}>Diperiksa:</p>
                </Grid>
                <Grid item xs={4}>
                  <img
                    id="inv-sign"
                    style={{ width: 120 }}
                    src={data.from_warehouse_info?.apj_signature}
                    alt="ttd"
                  />
                </Grid>
                <Grid item xs={12}>
                  {data.from_warehouse_info?.apj_title} :{' '}
                  {data.from_warehouse_info?.apj_name}
                </Grid>
                <Grid item xs={12}>
                  {data.from_warehouse_info?.apj_license_label} :{' '}
                  {data.from_warehouse_info?.apj_sipa}
                </Grid>
              </Grid>
            </S.SignatureSectionWrapper>
            <S.HandOverSectionWrapper item xs={4}>
              <Grid container>
                <Grid item xs={12}>
                  Barang ini diterima dalam kondisi baik
                </Grid>
                <Grid item xs={12}>
                  Diterima:
                </Grid>
                <Grid item xs={12}>
                  APJ Penerima:
                </Grid>
                <Grid item xs={12}>
                  SIPA:
                </Grid>
              </Grid>
            </S.HandOverSectionWrapper>
            <S.HandOverSectionWrapper item xs={3}>
              <Grid container>
                <Grid item xs={12}>
                  Tanggal terima:
                </Grid>
                <Grid item xs={12}>
                  Jam terima:
                </Grid>
              </Grid>
            </S.HandOverSectionWrapper>
          </S.SummarySectionWrapper>
        </>
      )}
    </S.PageBody>
  );

  return (
    <div>
      <S.PageWrapper>
        <TableContainer>
          <Table>
            {/* Header Section */}
            {renderHeader()}

            {/* Content Section */}
            {data.items.map((itemData, idx) =>
              renderBody(itemData, data.items.length === idx + 1),
            )}

            {/* Footer Section */}
            {renderFooter()}
          </Table>
        </TableContainer>
      </S.PageWrapper>
    </div>
  );
};
